import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { MailCheck, Loader2, CheckCircle2, Gift } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { API } from '../config';

// In-app email confirmation screen (opened from the "conferma la tua email" chat reminder)
export default function ConfirmEmailPage() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [sending, setSending] = useState(false);
  const [checking, setChecking] = useState(false);

  const resend = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${API}/auth/resend-verification`);
      toast.success(r.data?.message || 'Email di conferma inviata: controlla la posta (anche lo spam).');
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile inviare l'email, riprova");
    } finally { setSending(false); }
  };

  const recheck = async () => {
    setChecking(true);
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      if (data?.is_verified) await checkAuth();
      else toast.info("Non risulta ancora confermata: apri il link nella mail, poi riprova.");
    } catch { toast.error('Riprova tra poco'); } finally { setChecking(false); }
  };

  if (user?.is_verified) {
    return (
      <div className="min-h-screen bg-[#121212] flex flex-col items-center justify-center p-6 text-center" data-testid="confirm-email-done">
        <CheckCircle2 className="w-14 h-14 text-emerald-400 mb-4" />
        <h1 className="text-2xl font-black text-white mb-2">Email confermata</h1>
        <p className="text-sm text-gray-400 mb-6">Grazie! Il tuo amico riceverà il suo regalo 🎁</p>
        <button type="button" onClick={() => navigate('/dashboard', { replace: true })} className="px-6 py-3 rounded-xl bg-[#F5A623] text-[#1A1A1A] font-black" data-testid="confirm-email-home-btn">Vai alla Home</button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#121212] flex flex-col items-center justify-center p-6 text-center" data-testid="confirm-email-page">
      <div className="w-16 h-16 rounded-full bg-[#F5A623]/15 border border-[#F5A623]/40 flex items-center justify-center mb-5">
        <MailCheck className="w-8 h-8 text-[#F5A623]" />
      </div>
      <h1 className="text-2xl font-black text-white mb-2">Conferma la tua email</h1>
      <p className="text-sm text-gray-400 mb-1 max-w-xs">Ti abbiamo scritto a <strong className="text-white" data-testid="confirm-email-address">{user?.email}</strong>.</p>
      <p className="text-sm text-gray-400 mb-6 max-w-xs flex items-center justify-center gap-1.5"><Gift className="w-4 h-4 text-emerald-400" /> Apri la mail e tocca il link: il tuo amico riceverà il suo regalo.</p>
      <button type="button" onClick={resend} disabled={sending} className="w-full max-w-xs py-3 rounded-xl bg-[#F5A623] text-[#1A1A1A] font-black flex items-center justify-center gap-2 mb-3 disabled:opacity-60" data-testid="confirm-email-resend-btn">
        {sending && <Loader2 className="w-4 h-4 animate-spin" />} Rimandami l&apos;email
      </button>
      <button type="button" onClick={recheck} disabled={checking} className="w-full max-w-xs py-3 rounded-xl bg-white/5 border border-white/10 text-white font-bold flex items-center justify-center gap-2" data-testid="confirm-email-recheck-btn">
        {checking && <Loader2 className="w-4 h-4 animate-spin" />} Ho già confermato
      </button>
    </div>
  );
}
